import { computed, ref } from 'vue';
import { formatDateForApi } from '@/shared/lib';
import { type CreateOpenTrainingExternalIndividualInstructorPayload } from '@/entities/Training';
import { useCurriculumUploadRow, type CurriculumUploadStatus } from './useCurriculumUploadRow';
import { useNameTrainingDateRow } from './useNameTrainingDateRow';

export const MAX_EXTERNAL_INSTRUCTORS = 5;
export const MIN_EXTERNAL_INSTRUCTORS = 1;

export type { CurriculumUploadStatus };

export interface ExternalIndividualInstructorRow {
  id: number;
  cpf: string;
  profession: string;
  name: string;
  trainingDate: Date | null;
  curriculumObjectName: string | null;
  curriculumFileName: string | null;
  uploadProgress: number;
  uploadStatus: CurriculumUploadStatus;
  cpfError: string | null;
  professionError: string | null;
  nameError: string | null;
}

const ROW_FIELD_ERROR_PATTERN = /^externalIndividualInstructors\.(\d+)\.(cpf|profession|name)$/;

export function useOpenTrainingExternalIndividualInstructors() {
  let nextRowId = 1;

  function createEmptyRow(): ExternalIndividualInstructorRow {
    return {
      id: nextRowId++,
      cpf: '',
      profession: '',
      name: '',
      trainingDate: null,
      curriculumObjectName: null,
      curriculumFileName: null,
      uploadProgress: 0,
      uploadStatus: 'idle',
      cpfError: null,
      professionError: null,
      nameError: null,
    };
  }

  const rows = ref<ExternalIndividualInstructorRow[]>([createEmptyRow()]);
  const curriculumUpload = useCurriculumUploadRow(rows);
  const nameTrainingDateRow = useNameTrainingDateRow(rows);

  const canAddMore = computed(() => rows.value.length < MAX_EXTERNAL_INSTRUCTORS);
  const canRemove = computed(() => rows.value.length > MIN_EXTERNAL_INSTRUCTORS);

  const isComplete = computed(() =>
    rows.value.every(
      (row) =>
        row.cpf.trim() !== '' &&
        row.profession.trim() !== '' &&
        row.name.trim() !== '' &&
        row.trainingDate !== null &&
        row.uploadStatus !== 'uploading'
    )
  );

  function addRow(): void {
    if (rows.value.length < MAX_EXTERNAL_INSTRUCTORS) {
      rows.value.push(createEmptyRow());
    }
  }

  function removeRow(index: number): void {
    if (rows.value.length > MIN_EXTERNAL_INSTRUCTORS) {
      rows.value.splice(index, 1);
    }
  }

  function reset(): void {
    rows.value = [createEmptyRow()];
  }

  function setCpf(index: number, value: string): void {
    const row = rows.value[index];
    if (!row) return;
    row.cpf = value;
    row.cpfError = null;
  }

  function setProfession(index: number, value: string): void {
    const row = rows.value[index];
    if (!row) return;
    row.profession = value;
    row.professionError = null;
  }

  function applyFieldErrors(fieldErrors: Record<string, string[]>): void {
    for (const row of rows.value) {
      row.cpfError = null;
      row.professionError = null;
      row.nameError = null;
    }

    for (const [path, messages] of Object.entries(fieldErrors)) {
      const match = ROW_FIELD_ERROR_PATTERN.exec(path);
      const message = messages[0];
      if (!match || !message) continue;

      const row = rows.value[Number(match[1])];
      if (!row) continue;

      switch (match[2]) {
        case 'cpf':
          row.cpfError = message;
          break;
        case 'profession':
          row.professionError = message;
          break;
        case 'name':
          row.nameError = message;
          break;
      }
    }
  }

  function buildPayload(): CreateOpenTrainingExternalIndividualInstructorPayload[] | null {
    const result: CreateOpenTrainingExternalIndividualInstructorPayload[] = [];

    for (const row of rows.value) {
      if (!row.cpf.trim() || !row.profession.trim() || !row.name.trim() || row.trainingDate === null) {
        return null;
      }
      const trainingDate = formatDateForApi(row.trainingDate);
      if (trainingDate === null) return null;

      result.push({
        name: row.name.trim(),
        cpf: row.cpf.replace(/\D/g, ''),
        profession: row.profession.trim(),
        trainingDate,
        curriculumObjectName: row.curriculumObjectName ?? undefined,
      });
    }

    return result;
  }

  return {
    rows,
    canAddMore,
    canRemove,
    isComplete,
    addRow,
    removeRow,
    reset,
    setCpf,
    setProfession,
    setName: nameTrainingDateRow.setName,
    setTrainingDate: nameTrainingDateRow.setTrainingDate,
    selectCurriculumFile: curriculumUpload.selectCurriculumFile,
    removeCurriculumFile: curriculumUpload.removeCurriculumFile,
    buildPayload,
    applyFieldErrors,
  };
}
